import { Loading } from "../../app/Status";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";
import type { DirectoryRepliesResponse, DirectoryReplyDto } from "@klantpunt/shared";
import { IconMail } from "../../app/icons";
import { api } from "../../lib/api";
import { useToast } from "../../lib/toast";
import { companyPath } from "./parts";

const repliesKey = ["directory-replies"] as const;
const fmt = (iso: string) => new Date(iso).toLocaleString("nl-NL", { dateStyle: "medium", timeStyle: "short" });

/** Aantal ongelezen antwoorden op mails uit het adresboek (telt mee bij de Inbox in het menu). */
export function useDirectoryReplyCount(enabled: boolean) {
  return useQuery({
    queryKey: [...repliesKey, "ongelezen"],
    queryFn: () => api<DirectoryRepliesResponse>("/directory/replies"),
    enabled,
    refetchInterval: 60_000,
    select: (d) => d.unread,
  });
}

/** Waar het antwoord in het adresboek staat: bij het bedrijf, anders bij de persoon. */
const placeOf = (r: DirectoryReplyDto) =>
  r.company ? companyPath(r.company.id, "logboek") : r.person ? `/adresboek?persoon=${r.person.id}` : "/adresboek";

/**
 * Inbox › *Antwoorden* (docs/ADRESBOEK.md, "Antwoorden"): antwoorden op mails uit het adresboek. Het antwoord
 * staat in het logboek van het bedrijf; hier zie je dat het binnenkwam. Gelezen geldt voor de hele organisatie.
 */
export function RepliesList() {
  const qc = useQueryClient();
  const toast = useToast();
  const [all, setAll] = useState(false);
  const list = useQuery({
    queryKey: [...repliesKey, all ? "alle" : "ongelezen"],
    queryFn: () => api<DirectoryRepliesResponse>(`/directory/replies?status=${all ? "alle" : "ongelezen"}`),
    refetchInterval: 60_000,
  });
  const mark = async (r: DirectoryReplyDto, read: boolean) => {
    try {
      await api(`/directory/replies/${r.id}/read`, { method: "POST", body: { read } });
      await qc.invalidateQueries({ queryKey: repliesKey });
    } catch {
      toast("Dat lukte niet. Probeer het opnieuw.");
    }
  };

  return (
    <section className="panel intake-list" aria-label="Mailantwoorden">
      <label className="dir-check replies-all">
        <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} /> Ook gelezen antwoorden tonen
      </label>
      {list.isPending && <Loading />}
      {list.isError && (
        <div className="alert" role="alert">
          Antwoorden konden niet worden geladen.
        </div>
      )}
      {list.data && !list.data.items.length && (
        <p className="muted">
          {all
            ? "Nog geen antwoorden op mails uit het adresboek."
            : "Geen nieuwe antwoorden. Antwoorden op mails uit het adresboek komen hier binnen en in het logboek van het bedrijf."}
        </p>
      )}
      <ul className="intake-items">
        {list.data?.items.map((r) => (
          <li key={r.id} className={`reply-item ${r.readAt ? "read" : ""}`}>
            <span className="intake-thumb icon" aria-hidden="true">
              <IconMail />
            </span>
            <span className="grow">
              <span className="intake-title">
                {r.from.name ?? r.from.email}
                {r.company && <span className="muted"> · {r.company.name}</span>}
              </span>
              <span className="block intake-sub">{r.subject}</span>
              <span className="muted small block reply-snippet">{r.body.replace(/\s+/g, " ")}</span>
              <span className="muted small block">
                {fmt(r.createdAt)}
                {r.inReplyTo?.sentBy && ` · op de mail van ${r.inReplyTo.sentBy}`}
                {r.building && ` · ${r.building.name}`}
              </span>
              <span className="reply-actions">
                <Link className="btn small" to={placeOf(r)} onClick={() => !r.readAt && void mark(r, true)}>
                  Openen in adresboek
                </Link>
                <button type="button" className="btn small" onClick={() => void mark(r, !r.readAt)}>
                  {r.readAt ? "Markeer als ongelezen" : "Markeer als gelezen"}
                </button>
              </span>
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
